import { useState, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Table } from '@models/table';
import {
    useAddSystemTableOptionMutation,
    useEditSystemTableOptionByIdMutation,
    useGetSystemTableOptionsQuery,
} from '@features/tables/api/systemTables.api';
import { useErrorBoundary } from 'react-error-boundary';
import { handleErrorWithBoundary } from '@shared/lib/errors';

interface UseEditableSystemTableOptionsParams {
    tableNumber?: number | null;
    valueMode: 'index' | 'name';
    enabled: boolean;
}

interface UseEditableSystemTableOptionsResult {
    items: ReactNode[];
    /**
     * MUI <Select> renders the selected MenuItem's children verbatim in the closed
     * field by default. Since each row's children here include the (always-present,
     * hover-revealed) edit icon, that would bleed into the closed field and inflate
     * its height - so the caller must pass this as the Select's `renderValue` to get
     * plain-text-only display when closed.
     */
    renderValue: (value: unknown) => ReactNode;
}

/**
 * Returns a plain array of <MenuItem> elements (existing options, each with an
 * inline-editable name, plus a trailing "add new option" item) meant to be spread
 * directly as children of a MUI <Select>. It must NOT be wrapped in a component or
 * Fragment at the call site - <Select> only recognizes and wires up selection
 * handling for MenuItem elements that are direct children in the element tree, so
 * an intermediate component boundary would silently break clicking-to-select.
 *
 * None of the custom controls rendered inside a row (edit icon, inline inputs, the
 * "add new" row) may put their click handler directly on the <MenuItem> itself:
 * <Select> clones every MenuItem with its own click handler that calls ours first
 * and then unconditionally selects/closes regardless of stopPropagation (stopping
 * propagation only blocks separate ancestor DOM listeners, not this same handler's
 * own follow-up code). Handlers must instead live on an inner child element, where
 * stopPropagation genuinely prevents the click from ever reaching the MenuItem.
 */
export function useEditableSystemTableOptions({ tableNumber, valueMode, enabled }: UseEditableSystemTableOptionsParams): UseEditableSystemTableOptionsResult {

    const { showBoundary } = useErrorBoundary();

    const { data: options } = useGetSystemTableOptionsQuery(tableNumber ?? 0, { skip: !enabled || !tableNumber });
    const [editOption] = useEditSystemTableOptionByIdMutation();
    const [addOption] = useAddSystemTableOptionMutation();

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState<string>('');
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [newName, setNewName] = useState<string>('');
    const [busy, setBusy] = useState<boolean>(false);


    function startEdit(event: MouseEvent, option: Table): void {
        event.stopPropagation();
        setEditingId(option.id);
        setEditingName(option.name ?? '');
    }


    function cancelEdit(): void {
        setEditingId(null);
        setEditingName('');
    }


    async function commitEdit(option: Table, tableNumber: number): Promise<void> {

        const trimmedName = editingName.trim();

        if (busy || !trimmedName || trimmedName === (option.name ?? '')) {
            cancelEdit();
            return;
        }

        setBusy(true);

        try {
            await editOption({ systemTableNumber: tableNumber, option: { ...option, name: trimmedName } }).unwrap();
        }

        catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }

        finally {
            setBusy(false);
            cancelEdit();
        }
    }


    function cancelAdd(): void {
        setIsAdding(false);
        setNewName('');
    }


    async function commitAdd(tableNumber: number): Promise<void> {

        const trimmedName = newName.trim();

        if (busy || !trimmedName) {
            cancelAdd();
            return;
        }

        setBusy(true);

        try {
            const nextIndex = (options?.length ?? 0) + 1;
            const created = await addOption({ systemTableNumber: tableNumber, optionIndex: nextIndex }).unwrap();
            await editOption({ systemTableNumber: tableNumber, option: { ...created, name: trimmedName } }).unwrap();
        }

        catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }

        finally {
            setBusy(false);
            cancelAdd();
        }
    }


    function stopKeys(event: KeyboardEvent): void {
        event.stopPropagation();
    }


    function renderValue(value: unknown): ReactNode {

        if (value === '' || value === null || value === undefined) return '';

        if (valueMode === 'name') return value as string;

        const index = (options ?? []).findIndex((option) => option.index === value);

        if (index < 0) return '';

        return `${index + 1}. ${options![index].name ?? ''}`;
    }


    if (!enabled || !tableNumber) return { items: [], renderValue };


    const items: ReactNode[] = (options ?? []).map((option, index) => (
        <MenuItem
            key={option.id}
            value={valueMode === 'index' ? option.index : (option.name || '')}
            sx={{ '&:hover .system-table-edit-btn': { opacity: 1 } }}
        >
            {
                editingId === option.id ?

                    <Box
                        sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <InputBase
                            autoFocus
                            fullWidth
                            value={editingName}
                            disabled={busy}
                            onChange={(event) => setEditingName(event.target.value)}
                            onBlur={() => commitEdit(option, tableNumber)}
                            onKeyDown={(event) => {
                                stopKeys(event);
                                if (event.key === 'Enter') commitEdit(option, tableNumber);
                                if (event.key === 'Escape') cancelEdit();
                            }}
                        />
                    </Box>

                    :

                    <>
                        <Box sx={{ flex: 1 }}>{index + 1}. {option.name}</Box>
                        <IconButton
                            className="system-table-edit-btn"
                            size="small"
                            tabIndex={-1}
                            sx={{ opacity: 0, transition: 'opacity .15s' }}
                            onClick={(event) => startEdit(event, option)}
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <EditIcon fontSize="inherit" />
                        </IconButton>
                    </>
            }
        </MenuItem>
    ));

    items.push(
        <MenuItem
            key="__add_system_table_option__"
            disableRipple
            sx={{ borderTop: 1, borderColor: 'divider', padding: 0 }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', width: '100%', padding: '6px 16px' }}
                onClick={(event) => {
                    event.stopPropagation();
                    if (!isAdding) setIsAdding(true);
                }}
                onMouseDown={(event) => event.stopPropagation()}
            >
                {
                    isAdding ?

                        <InputBase
                            autoFocus
                            fullWidth
                            placeholder="שם חדש"
                            value={newName}
                            disabled={busy}
                            onChange={(event) => setNewName(event.target.value)}
                            onBlur={() => commitAdd(tableNumber)}
                            onKeyDown={(event) => {
                                stopKeys(event);
                                if (event.key === 'Enter') commitAdd(tableNumber);
                                if (event.key === 'Escape') cancelAdd();
                            }}
                        />

                        :

                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                            <AddIcon fontSize="small" sx={{ marginInlineEnd: 1 }} />
                            הוספת אפשרות
                        </Box>
                }
            </Box>
        </MenuItem>
    );

    return { items, renderValue };
}
